import React, { useEffect } from 'react';
import { graphql } from 'gatsby';
import SEO from '@components/Common/SEO/index';
import NavigationV2 from '@components/NavigationV2/index';
import FooterV2 from '@components/FooterV2/index';
import BottomStrip from '@components/PPCNavbar/BottomStrip';
import FAQSection from '@components/v5/FAQSection/index';
import useSale from '@src/hooks/useSale';
import { GTM } from '@src/analytics/gtm';
import { gtmEvents } from '@src/analytics/gtmEventsConstant';

import * as styles from '@components/v5/AiEngineerFellowship/aiEngineerFellowship.module.scss';
import Hero from '@components/v5/AiEngineerFellowship/Hero';
import DeployedShowcase from '@components/v5/AiEngineerFellowship/DeployedShowcase';
import GradsAndStats from '@components/v5/AiEngineerFellowship/GradsAndStats';
import WhoItsFor from '@components/v5/AiEngineerFellowship/WhoItsFor';
import WhyAIEngineering from '@components/v5/AiEngineerFellowship/WhyAIEngineering';
import TechMarquee from '@components/v5/AiEngineerFellowship/TechMarquee';
import Pillars from '@components/v5/AiEngineerFellowship/Pillars';
import Curriculum from '@components/v5/AiEngineerFellowship/Curriculum';
import ProjectsTabs from '@components/v5/AiEngineerFellowship/ProjectsTabs';
import FDE from '@components/v5/AiEngineerFellowship/FDE';
import Differentiators from '@components/v5/AiEngineerFellowship/Differentiators';
import IncludedInFee from '@components/v5/AiEngineerFellowship/IncludedInFee';
import Mentors from '@components/v5/AiEngineerFellowship/Mentors';
import CareerOutcomes from '@components/v5/AiEngineerFellowship/CareerOutcomes';
import FeeSection from '@components/v5/AiEngineerFellowship/FeeSection';
import Testimonials from '@components/v5/AiEngineerFellowship/Testimonials';
import Downloads from '@components/v5/AiEngineerFellowship/Downloads';
import FinalCta from '@components/v5/AiEngineerFellowship/FinalCta';

const PROGRAM_ID = 'AI_ENGINEER_FELLOWSHIP';
const SLUG = 'ai-engineering';

// Content lives in src/yaml-pages/ai-engineering-pages/ai-engineering.yml
// (editable without touching code, matching how the other fellowship pages
// source their copy) and flows into each section below as props. Only the
// DeployedShowcase "window" mockups (interactive chat/diff/waveform visuals)
// stay hardcoded in that component — they're UI, not editable content.
export default function AiEngineerFellowshipProgram({ data }) {
  const { sale } = useSale();
  const content = data.aiEngineeringPagesYaml;

  useEffect(() => {
    GTM.track(gtmEvents.PAGE_VIEW_FSD, {
      type: PROGRAM_ID,
      slug: SLUG,
    });
    GTM.track(gtmEvents.PAGE_VIEW, {
      type: PROGRAM_ID,
      slug: SLUG,
    });
  }, []);

  // Scoped smooth-scroll for this page's own anchor nav (e.g. "See problem → production"),
  // without touching the global html element for every other page on the site.
  useEffect(() => {
    const previous = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = 'smooth';
    return () => {
      document.documentElement.style.scrollBehavior = previous;
    };
  }, []);

  return (
    <div id="top">
      <SEO
        title={content.SEOTitle}
        description={content.SEODescription}
        canonicalUrl={content.canonicalUrl}
      />

      <NavigationV2 program={PROGRAM_ID} sale={sale} hideCTA />

      {/* Hero stays outside the ported .page styles so the reused lead-form component's
          own styling can never be affected by the page-scoped resets below. It already
          reserves top space for the fixed nav itself (matching FspStylePageHero's own
          pt-[160px]/pt-60 padding), so no separate spacer div is needed here. */}
      <Hero sale={sale} content={content.hero} />

      <div className={styles.page}>
        <DeployedShowcase apps={content.deployedShowcase.apps} />
        <GradsAndStats content={content.gradsAndStats} />
        <WhoItsFor content={content.whoItsFor} />
        <WhyAIEngineering content={content.whyAIEngineering} />
        <TechMarquee rows={content.techMarquee.rows} />
        <Pillars pillars={content.pillars} />
        <Curriculum content={content.curriculum} />
        <ProjectsTabs content={content.projectsTabs} />
        <FDE steps={content.fde} />
        <Differentiators content={content.differentiators} />
        <IncludedInFee items={content.includedInFee} />
        <Mentors mentors={content.mentors} how={content.mentorsHow} />
        <CareerOutcomes content={content.careerOutcomes} />
        <FeeSection content={content.feeSection} />
        <Testimonials quotes={content.testimonials} />
        <Downloads content={content.downloads} />
      </div>

      {/* FAQSection is reused as-is (see Hero note above), so it stays outside .page too. */}
      <FAQSection id="faq" data={content.faqs} />

      <div className={styles.page}>
        <FinalCta steps={content.finalCta.steps} />
      </div>

      <FooterV2 />
      <BottomStrip />
    </div>
  );
}

export const query = graphql`
  query {
    aiEngineeringPagesYaml {
      SEOTitle
      SEODescription
      canonicalUrl
      hero {
        titleHtml
        subtitle
        descriptionHtml
        tags {
          icon
          label
        }
      }
      gradsAndStats {
        logos {
          title
          src
          maxHeight
        }
        stats {
          n
          l
        }
      }
      whoItsFor {
        cards {
          icon
          k
          h
          p
        }
        prereqs
        prework
      }
      whyAIEngineering {
        stats {
          n
          l
          src
        }
        roles
      }
      techMarquee {
        rows {
          label
          dir
          duration
          items {
            mono
            icon
            lucideIcon
            name
            tag
            cls
            soft
          }
        }
      }
      pillars {
        k
        h
        p
      }
      curriculum {
        sprints {
          id
          title
          wk
          lead
          pts
          ship
          chips
          highlight
        }
        dsaModules {
          name
          dur
        }
        systemDesignModules {
          name
          dur
        }
      }
      deployedShowcase {
        apps {
          id
          icon
          title
          blurb
          tag
          role
          description
          meta
          ships
          isCap
        }
      }
      projectsTabs {
        miniProjects {
          num
          icon
          title
          tag
          p
        }
        guidedProjects {
          badge
          title
          role
          p
          chips
          ships
        }
        capstoneIdeas
        optionalProjects {
          k
          title
          p
        }
      }
      fde {
        k
        h
        p
      }
      differentiators {
        stats {
          n
          l
        }
        features {
          icon
          h
          p
        }
      }
      includedInFee {
        icon
        h
      }
      mentors {
        id
        name
        role
        bio
      }
      mentorsHow {
        h
        p
      }
      careerOutcomes {
        steps {
          icon
          when
          h
          p
          target
        }
        portfolioApps {
          icon
          label
        }
        skills
      }
      feeSection {
        amount
        includedLeft
        includedRight
      }
      testimonials {
        quote
        name
        role
        company
        domain
      }
      downloads {
        heading
        subheading
      }
      finalCta {
        steps {
          h
          p
        }
      }
      faqs {
        question
        answer
      }
    }
  }
`;
